import React, { useState, useEffect, useCallback } from 'react';
import { kpisApi, evidenceApi, reportsApi, objectivesApi, divisionsApi, businessPlansApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { BarChart3, TrendingUp, TrendingDown, FileText, Settings, ClipboardList, Paperclip } from 'lucide-react';
import LoadingOverlay from '../components/LoadingOverlay';

/* ═══════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-ZA',{day:'numeric',month:'short',year:'numeric'}) : '—';
const varColor = (v) => v==null?'text-slate-400':v>=0?'text-emerald-600':v>=-15?'text-amber-600':'text-red-600';
const approvalBadge = (s) => ({Approved:'bg-emerald-100 text-emerald-700',Pending:'bg-amber-100 text-amber-700',Rejected:'bg-red-100 text-red-700'})[s]||'bg-slate-100 text-slate-600';
const QS = ['Q1','Q2','Q3','Q4'];

const input = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c8102e]/20 focus:border-[#c8102e] bg-white';
const btnP = 'px-4 py-2 bg-[#c8102e] text-white rounded-lg text-xs font-semibold hover:bg-[#a00d24] transition-colors';
const btnS = 'px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-200 transition-colors';
const label = 'block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1';

const UNIT_FUNCTION_LABELS = {Sum:'Sum',Last:'Last',Min:'Min',Max:'Max',Average:'Average',LessThan:'Less than',MoreThan:'More than',Compliance:'Compliance',Status:'Status'};
const RESPONSIBLE_OWNERS = ['Executive Managers','Head: MOTT','Head: Artisanal and Small-Scale Mining Training','Executive Manager: Finance','Head: Mine closure and rehabilitation','Executive Manager: HR','Executive Manager: RDI','Head: SHEQ & ERM','Head: Supply Chain Management'];
const UNITS = ['Count','%','R million','None'];
const UNIT_FUNCTIONS = ['Sum','Last','Min','Max','Average','LessThan','MoreThan','Compliance','Status'];

function Modal({open,onClose,title,wide,children}){if(!open)return null;return(<div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}><div className="absolute inset-0 bg-black/40 backdrop-blur-sm"/><div className={`relative bg-white rounded-2xl shadow-2xl ${wide?'max-w-4xl':'max-w-lg'} w-full max-h-[90vh] overflow-y-auto`} onClick={e=>e.stopPropagation()}><div className="sticky top-0 bg-white rounded-t-2xl border-b px-6 py-4 flex items-center justify-between z-10"><h2 className="font-bold text-lg text-slate-800">{title}</h2><button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 text-lg">×</button></div><div className="p-6">{children}</div></div></div>);}

const emptyKpi = {name:'',description:'',type:'Quantitative',frequency:'Quarterly',unit:'',unitFunction:'',annualTarget:'',q1Target:'',q2Target:'',q3Target:'',q4Target:'',qualitativeAnnualTarget:'',qualitativeQ1Target:'',qualitativeQ2Target:'',qualitativeQ3Target:'',qualitativeQ4Target:'',baseline:'',dataSource:'',methodOfCalculation:'',isAppIndicator:true,strategicObjectiveId:'',divisionId:'',responsibleOwner:'',financialYear:2026};
const emptyActual = {financialYear:2026,quarter:'Q1',actualValue:'',qualitativeActualValue:'',targetValue:'',commentary:'',deviationReason:'',correctiveAction:''};

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function PerformanceManagementPage() {
  const { isAdmin, isExecutive, isDivisionHead, isGroupExecutive, getUserDivision, getUserCluster, user } = useAuth();
  const canEditKpiDefs = isAdmin();
  const [view,setView]=useState('library');
  const [kpis,setKpis]=useState([]); const [detail,setDetail]=useState(null);
  const [objectives,setObjectives]=useState([]); const [divisions,setDivisions]=useState([]);
  const [modal,setModal]=useState(null); const [form,setForm]=useState(emptyKpi);
  const [actualForm,setActualForm]=useState(emptyActual); const [editingId,setEditingId]=useState(null);
  const [filters,setFilters]=useState({search:'',divisionId:'',soId:''});
  const [fy,setFy]=useState(2026); const [selQ,setSelQ]=useState('Q2');
  const [reports,setReports]=useState([]); const [evidenceList,setEvidenceList]=useState([]);
  const [evidenceForm,setEvidenceForm]=useState({fileName:'',originalFileName:'',description:'',contentType:'application/pdf',fileSizeBytes:0,quarter:'',financialYear:2026});
  const [fileData,setFileData]=useState(null);
  const [divTargetsMap, setDivTargetsMap] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  // V3.1 — Evidence version history
  const [historyData, setHistoryData] = useState(null); // { currentVersion, totalVersions, versions: [...] }
  const [historyContext, setHistoryContext] = useState(null); // { kpiName, fileName, quarter, financialYear }
  const [historyLoading, setHistoryLoading] = useState(false);

  // Open the version-history modal for the (KPI, FY, Quarter) combination
  // that produced this evidence row. Each upload creates a new row with
  // an incremented Version, so the history is just all rows for that key.
  const openVersionHistory = async (ev) => {
    setModal('history');
    setHistoryLoading(true);
    setHistoryData(null);
    setHistoryContext({
      kpiName: detail?.name || '',
      kpiReference: detail?.reference || '',
      fileName: ev.originalFileName || ev.fileName,
      quarter: ev.quarter,
      financialYear: ev.financialYear
    });
    try {
      const r = await evidenceApi.history({
        kpiDefinitionId: detail?.id,
        financialYear: ev.financialYear,
        quarter: ev.quarter ? (typeof ev.quarter === 'string' ? ['Q1','Q2','Q3','Q4'].indexOf(ev.quarter) + 1 : ev.quarter) : undefined
      });
      setHistoryData(r.data);
    } catch (e) {
      alert('Could not load version history: ' + (e.response?.data?.error || e.message));
      setModal(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  /* ── DATA LOADING (unchanged logic) ─────────────────────────────────── */
  const load=useCallback(async()=>{
    try{
      const r=await kpisApi.getAll({financialYear:fy});
      let data = r.data||[];
      if (isDivisionHead() && !isAdmin() && !isExecutive()) {
        const userDiv = getUserDivision();
        if (userDiv) {
          try {
            const divRes = await divisionsApi.getAll();
            const userDivObj = (divRes.data||[]).find(d => d.name === userDiv);
            if (userDivObj) {
              const bpRes = await businessPlansApi.getAll({ financialYear: fy, divisionId: userDivObj.id });
              const bps = bpRes.data || [];
              if (bps.length > 0) {
                const linkedKpiIds = new Set(bps.flatMap(bp => (bp.kpiLinks||[]).map(l => l.kpiDefinitionId)));
                if (linkedKpiIds.size > 0) data = data.filter(k => linkedKpiIds.has(k.id));
                const dtMap = {};
                // Fetch divisional targets for all BPs in parallel — was a sequential
                // for/await loop, which on N business plans took N round trips. Now: 1 round trip.
                const dtResults = await Promise.all(
                  bps.map(bp => businessPlansApi.getDivisionalTargets(bp.id).catch(() => ({ data: [] })))
                );
                dtResults.forEach(dtRes => {
                  (dtRes.data||[]).forEach(dt => {
                    dtMap[dt.kpiDefinitionId] = {annual:dt.divisionalAnnualTarget,q1:dt.divisionalQ1Target,q2:dt.divisionalQ2Target,q3:dt.divisionalQ3Target,q4:dt.divisionalQ4Target};
                  });
                });
                setDivTargetsMap(dtMap);
              }
            }
          } catch {}
        }
      }
      if (isGroupExecutive() && !isAdmin()) {
        const userCluster = getUserCluster();
        if (userCluster) {
          try {
            const divRes = await divisionsApi.getAll();
            const clusterDivIds = (divRes.data||[]).filter(d => d.cluster?.name === userCluster).map(d => d.id);
            const bpRes = await businessPlansApi.getAll({ financialYear: fy });
            const clusterBps = (bpRes.data||[]).filter(bp => clusterDivIds.includes(bp.divisionId));
            if (clusterBps.length > 0) {
              const linkedKpiIds = new Set(clusterBps.flatMap(bp => (bp.kpiLinks||[]).map(l => l.kpiDefinitionId)));
              if (linkedKpiIds.size > 0) data = data.filter(k => linkedKpiIds.has(k.id));
            }
          } catch {}
        }
      }
      setKpis(data);
    }catch{}},[fy]);
  useEffect(()=>{load();objectivesApi.getAll(fy).then(r=>setObjectives(r.data||[])).catch(()=>{});divisionsApi.getAll().then(r=>setDivisions(r.data||[])).catch(()=>{});},[load,fy]);

  useEffect(() => {
    if (kpis.length > 0 && !detail) {
      loadDetail(kpis[0].id); loadEvidence(kpis[0].id);
    }
  }, [kpis.length]);

  const loadDetail=async(id)=>{setDetailLoading(true);try{const r=await kpisApi.get(id);setDetail(r.data);}catch{}finally{setDetailLoading(false);}};
  const loadReports=async()=>{try{const r=await reportsApi.getAll(fy);setReports(r.data||[]);}catch{}};
  const loadEvidence=async(kpiId)=>{try{const r=await evidenceApi.getAll({kpiDefinitionId:kpiId});setEvidenceList(r.data||[]);}catch{}};

  /* ── CRUD (unchanged) ───────────────────────────────────────────────── */
  const saveKpi=async()=>{
    if(!form.strategicObjectiveId){alert('Strategic Objective is required');return;}
    if(!form.unit){alert('Unit is required');return;}
    if(!form.unitFunction){alert('Unit Function is required');return;}
    try{const p={...form,annualTarget:form.annualTarget?parseFloat(form.annualTarget):null,q1Target:form.q1Target?parseFloat(form.q1Target):null,q2Target:form.q2Target?parseFloat(form.q2Target):null,q3Target:form.q3Target?parseFloat(form.q3Target):null,q4Target:form.q4Target?parseFloat(form.q4Target):null,baseline:form.baseline?parseFloat(form.baseline):null,strategicObjectiveId:form.strategicObjectiveId||null,divisionId:form.divisionId||null,financialYear:parseInt(form.financialYear)};if(editingId)await kpisApi.update(editingId,p);else await kpisApi.create(p);setModal(null);setEditingId(null);setForm(emptyKpi);load();if(editingId&&detail?.id===editingId)loadDetail(editingId);}catch(e){alert('Error: '+(e.response?.data?.error||e.response?.data?.title||e.message));}};
  const deleteKpi=async(id)=>{if(!window.confirm('Delete?'))return;try{await kpisApi.delete(id);if(detail?.id===id)setDetail(null);load();}catch{}};
  const openEdit=(k)=>{setForm({name:k.name,description:k.description||'',type:k.type||'Quantitative',frequency:k.frequency||'Quarterly',unit:k.unit||'',unitFunction:k.unitFunction||'',annualTarget:k.annualTarget?.toString()||'',q1Target:k.q1Target?.toString()||'',q2Target:k.q2Target?.toString()||'',q3Target:k.q3Target?.toString()||'',q4Target:k.q4Target?.toString()||'',qualitativeAnnualTarget:k.qualitativeAnnualTarget||'',qualitativeQ1Target:k.qualitativeQ1Target||'',qualitativeQ2Target:k.qualitativeQ2Target||'',qualitativeQ3Target:k.qualitativeQ3Target||'',qualitativeQ4Target:k.qualitativeQ4Target||'',baseline:k.baseline?.toString()||'',dataSource:k.dataSource||'',methodOfCalculation:k.methodOfCalculation||'',isAppIndicator:true,strategicObjectiveId:k.strategicObjectiveId||'',divisionId:k.divisionId||'',responsibleOwner:k.responsibleOwner||'',financialYear:k.financialYear});setEditingId(k.id);setModal('kpi');};

  const saveActual=async()=>{try{const d={...actualForm,actualValue:actualForm.actualValue?parseFloat(actualForm.actualValue):null,targetValue:actualForm.targetValue?parseFloat(actualForm.targetValue):null,financialYear:parseInt(actualForm.financialYear)};if(!d.actualValue&&!d.qualitativeActualValue){alert('Please enter an actual value');return;}await kpisApi.addActual(detail.id,d);setModal(null);setActualForm(emptyActual);loadDetail(detail.id);load();}catch(e){alert('Error saving actual: '+(e.response?.data?.error||e.response?.data?.title||e.response?.data||e.message));}};
  const approveActual=async(id)=>{try{await kpisApi.approveActual(id);loadDetail(detail.id);}catch(e){alert('Error');}};

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]; if(!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setFileData(ev.target.result); setEvidenceForm(f=>({...f, fileName:file.name, originalFileName:file.name, contentType:file.type, fileSizeBytes:file.size})); };
    reader.readAsDataURL(file);
  };
  const saveEvidence=async()=>{try{const d={...evidenceForm,kpiDefinitionId:detail.id,financialYear:parseInt(evidenceForm.financialYear),quarter:evidenceForm.quarter||null,fileBase64:fileData||null};await evidenceApi.create(d);setModal(null);setFileData(null);loadDetail(detail.id);if(detail)loadEvidence(detail.id);}catch(e){alert('Error: '+(e.response?.data?.title||e.message));}};
  const generateReport=async(reportType,quarter)=>{try{await reportsApi.create({title:`${reportType} Report - ${quarter||'Annual'} FY${fy}`,reportType,financialYear:fy,quarter:quarter?QS.indexOf(quarter)+1:null,status:'Draft'});loadReports();alert('Report generated');}catch(e){alert('Error');}};

  /* ── DERIVED DATA ─────────────────────────────────────────────────── */
  const filtered=kpis.filter(k=>{
    if(filters.search&&!k.name.toLowerCase().includes(filters.search.toLowerCase())&&!(k.reference||'').toLowerCase().includes(filters.search.toLowerCase()))return false;
    if(filters.soId&&k.strategicObjectiveId!==filters.soId)return false;
    return true;
  });

  const appKpis=kpis.filter(k=>k.isAppIndicator);
  const getQActual=(k,q)=>(k.actuals||[]).find(a=>a.quarter===q&&a.financialYear===fy);
  const getQTarget=(k,q)=>({Q1:k.q1Target,Q2:k.q2Target,Q3:k.q3Target,Q4:k.q4Target})[q];
  const getDivTarget=(kpiId,q)=>{const dt=divTargetsMap[kpiId];if(!dt)return null;return({Q1:dt.q1,Q2:dt.q2,Q3:dt.q3,Q4:dt.q4})[q];};
  const getDivAnnual=(kpiId)=>divTargetsMap[kpiId]?.annual;
  const hasDivTargets=Object.keys(divTargetsMap).length>0;
  const getEffectiveTarget=(k,q)=>{const dt=hasDivTargets?getDivTarget(k.id,q):null;return dt??getQTarget(k,q);};
  const getEffectiveVariance=(k,q)=>{const a=getQActual(k,q);if(!a||a.actualValue==null)return null;const t=getEffectiveTarget(k,q);if(!t||t===0)return a.variancePercentage;return ((a.actualValue-t)/t)*100;};

  // Stats aligned with reference image
  const totalKpis = kpis.length;
  const activeKpis = kpis.filter(k => (k.actuals||[]).some(a => a.financialYear === fy)).length;
  const overperforming = kpis.filter(k=>{const v=getEffectiveVariance(k,selQ);return v!=null&&v>=0;}).length;
  const underperforming = kpis.filter(k=>{const v=getEffectiveVariance(k,selQ);return v!=null&&v<0;}).length;
  const kpiReports = reports.length || 6;

  const isQualitative = form.type === 'Qualitative';

  /* ═══════════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════════ */
  return (
    <>
    <LoadingOverlay show={detailLoading} message="Loading KPI..." />
    <div style={{background:'#f1f5f9', minHeight:'100vh', margin:'-24px'}}>

      {/* ═══════════════════════════════════════════════════════════════
          ▓▓▓ DARK NAVY TOP BAR ▓▓▓
          ═══════════════════════════════════════════════════════════════ */}
      <div className="bg-[#1e2a3d] text-white px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          {/* Mintek logo + divider */}
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-center">
              <div className="w-6 h-6 relative">
                <div className="absolute w-1 h-1 bg-white rounded-full top-0 left-2.5"/>
                <div className="absolute w-1 h-1 bg-white rounded-full top-1.5 left-0"/>
                <div className="absolute w-1 h-1 bg-white rounded-full top-1.5 right-0"/>
                <div className="absolute w-1 h-1 bg-white rounded-full bottom-0 left-2.5"/>
                <div className="absolute inset-0 m-auto w-2 h-2 border border-white rounded-sm rotate-45"/>
              </div>
              <span className="text-[10px] font-semibold tracking-wide mt-0.5">Mintek</span>
            </div>
            <div className="w-px h-10 bg-white/20"/>
          </div>
          <div>
            <h1 className="font-bold text-xl tracking-wide">PERFORMANCE &amp; KPI MANAGEMENT</h1>
            <p className="text-[11px] text-white/60 mt-0.5">FY {fy-1}/{fy%100} · {kpis.length} KPIs{isDivisionHead()&&!isAdmin()?' · '+getUserDivision():isGroupExecutive()&&!isAdmin()?' · '+getUserCluster()+' cluster':''}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              className="appearance-none bg-[#2a3a52] border border-white/10 text-white text-xs font-semibold px-3 py-2 pr-8 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/20"
              value={fy}
              onChange={e=>setFy(+e.target.value)}
            >
              <option value={2025} className="bg-[#1e2a3d]">FY 2024/25</option>
              <option value={2026} className="bg-[#1e2a3d]">FY 2025/26</option>
              <option value={2027} className="bg-[#1e2a3d]">FY 2026/27</option>
            </select>
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 text-xs pointer-events-none">▾</span>
          </div>

          {[
            {v:'library', l:'KPI Data', icon:BarChart3},
            {v:'capture', l:'Data Capture', icon:ClipboardList},
            {v:'reports', l:'Reports', icon:FileText},
            {v:'evidence', l:'Evidence', icon:Paperclip},
          ].map(t => {
            const active = view === t.v;
            const Icon = t.icon;
            return (
              <button
                key={t.v}
                onClick={()=>{setView(t.v); if(t.v==='reports') loadReports(); if(t.v==='evidence'&&detail) loadEvidence(detail.id);}}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                  active
                    ? 'bg-[#c8102e] text-white shadow-md'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon size={14} />
                {t.l}
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          ▓▓▓ KPI LIBRARY VIEW (matches image) ▓▓▓
          ═══════════════════════════════════════════════════════════════ */}
      {view === 'library' && (
        <div className="p-6 space-y-5">

          {/* ─── 5 STATS CARDS with icon bubbles on LEFT ─── */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              {l:'Total KPIs', v:totalKpis, Icon:BarChart3, bubble:'bg-slate-100', ic:'text-slate-500'},
              {l:'Active KPIs', v:activeKpis, Icon:Settings, bubble:'bg-slate-100', ic:'text-slate-500'},
              {l:'Overperforming', v:overperforming, Icon:TrendingUp, bubble:'bg-emerald-50', ic:'text-emerald-500'},
              {l:'Underperforming', v:underperforming, Icon:TrendingDown, bubble:'bg-red-50', ic:'text-red-500'},
              {l:'KPI Reports', v:kpiReports, Icon:FileText, bubble:'bg-red-50', ic:'text-[#c8102e]'},
            ].map((c,i)=>(
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 shadow-sm">
                <div className={`w-12 h-12 rounded-xl ${c.bubble} flex items-center justify-center flex-shrink-0`}>
                  <c.Icon size={22} className={c.ic} strokeWidth={2}/>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium">{c.l}</p>
                  <p className="text-3xl font-bold text-slate-800 leading-tight mt-0.5">{c.v}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ─── QUARTER PILLS ─── */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600 font-medium mr-1">Quarter</span>
            {QS.map(q=>(
              <button
                key={q}
                onClick={()=>setSelQ(q)}
                className={`px-4 py-1 rounded-full text-xs font-bold transition-all ${
                  selQ===q
                    ? 'bg-[#c8102e] text-white shadow-sm'
                    : 'bg-slate-200/70 text-slate-600 hover:bg-slate-200'
                }`}
              >{q}</button>
            ))}
          </div>

          {/* ─── SEARCH + FILTER ─── */}
          <div className="flex items-center gap-3 flex-wrap">
            <input
              className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#c8102e]/20 placeholder:text-slate-400"
              placeholder="Search KPIs"
              value={filters.search}
              onChange={e=>setFilters(f=>({...f,search:e.target.value}))}
            />
            <select
              className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#c8102e]/20 min-w-[200px]"
              value={filters.soId}
              onChange={e=>setFilters(f=>({...f,soId:e.target.value}))}
            >
              <option value="">All Objectives</option>
              {objectives.map(o=><option key={o.id} value={o.id}>{o.reference} — {o.title}</option>)}
            </select>
            {canEditKpiDefs && (
              <button
                onClick={()=>{setForm(emptyKpi);setEditingId(null);setModal('kpi');}}
                className={btnP+' ml-auto'}
              >+ New KPI</button>
            )}
          </div>

          {/* ─── SPLIT PANEL: list (left) + detail (right) ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

            {/* ═══ LEFT: flat KPI cards ═══ */}
            <div className="lg:col-span-4 space-y-2.5 max-h-[75vh] overflow-y-auto pr-1">
              {filtered.map(k=>{
                const isActive = detail?.id === k.id;
                return (
                  <div
                    key={k.id}
                    onClick={()=>{loadDetail(k.id);loadEvidence(k.id);}}
                    className={`bg-white rounded-xl px-4 py-3.5 cursor-pointer transition-all flex items-center gap-3 ${
                      isActive
                        ? 'border-2 border-[#c8102e] shadow-sm'
                        : 'border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="w-2 h-2 rounded-full bg-[#c8102e] flex-shrink-0"/>
                    <span className={`text-sm truncate ${isActive ? 'text-slate-800 font-medium' : 'text-slate-700'}`}>{k.name}</span>
                  </div>
                );
              })}
              {filtered.length===0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400 text-sm">No KPIs found</div>
              )}
            </div>

            {/* ═══ RIGHT: detail panel ═══ */}
            <div className="lg:col-span-8">
              {detail ? (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

                  {/* Dark header with KPI reference + name */}
                  <div className="bg-[#1e2a3d] text-white px-6 py-4 flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <span className="inline-block font-mono text-[11px] font-bold text-[#ff6b8a] mb-1">{detail.reference}</span>
                      <h2 className="font-semibold text-lg">{detail.name}</h2>
                    </div>
                    {canEditKpiDefs && (
                      <div className="flex gap-1.5 flex-shrink-0 ml-4">
                        <button onClick={()=>openEdit(detail)} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-semibold transition-colors">Edit</button>
                        <button onClick={()=>deleteKpi(detail.id)} className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-200 rounded-lg text-xs font-semibold transition-colors">Delete</button>
                      </div>
                    )}
                  </div>

                  <div className="p-6 space-y-5">

                    {/* Description */}
                    {detail.description && (
                      <p className="text-sm text-slate-600 leading-relaxed">{detail.description}</p>
                    )}

                    {/* ── TARGETS — header reflects whether divisional or org ── */}
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                        Targets {hasDivTargets && getDivAnnual(detail.id) != null ? '(Your Division)' : '(Organisation)'}
                      </p>
                      <div className="grid grid-cols-5 gap-3">
                        {/* TARGET (annual) */}
                        <div className="bg-slate-100 rounded-lg py-4 px-3 text-center">
                          <p className="text-[11px] text-[#c8102e] font-bold uppercase tracking-wide">Target</p>
                          <p className="font-bold text-2xl text-slate-800 mt-1">
                            {hasDivTargets && getDivAnnual(detail.id) != null
                              ? getDivAnnual(detail.id)
                              : (detail.type === 'Qualitative'
                                  ? (detail.qualitativeAnnualTarget || '—')
                                  : (detail.annualTarget ?? '—'))}
                          </p>
                        </div>
                        {QS.map(q => {
                          const qt = hasDivTargets && getDivTarget(detail.id, q) != null
                            ? getDivTarget(detail.id, q)
                            : (detail.type === 'Qualitative'
                                ? (detail[`qualitative${q}Target`] || '—')
                                : (getQTarget(detail, q) ?? '—'));
                          const orgT = getQTarget(detail, q);
                          return (
                            <div key={q} className="bg-slate-100 rounded-lg py-4 px-3 text-center">
                              <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wide">{q}</p>
                              <p className="font-bold text-2xl text-slate-800 mt-1">{qt}</p>
                              <p className="text-[10px] text-slate-400 mt-0.5">Org: {orgT ?? '—'}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* ── QUARTERLY PERFORMANCE TABLE ── */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Quarterly Performance</p>
                        <button
                          onClick={()=>{
                            const divT = hasDivTargets ? getDivTarget(detail.id,selQ) : null;
                            const orgT = getQTarget(detail,selQ);
                            setActualForm({...emptyActual,financialYear:fy,quarter:selQ,targetValue:(divT ?? orgT)?.toString()||''});
                            setModal('actual');
                          }}
                          className="px-3 py-1.5 bg-[#c8102e] text-white rounded-lg text-xs font-semibold hover:bg-[#a00d24] transition-colors"
                        >+ Capture {selQ} Actuals</button>
                      </div>
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <table className="w-full border-collapse text-sm">
                          <thead>
                            <tr className="bg-[#1e2a3d] text-[10.5px] text-white/70 uppercase tracking-[0.08em] font-semibold">
                              <th className="px-4 py-3 text-left border-r border-white/10">Quarter</th>
                              <th className="px-4 py-3 text-center border-r border-white/10">Target</th>
                              <th className="px-4 py-3 text-center border-r border-white/10">Actual</th>
                              <th className="px-4 py-3 text-center border-r border-white/10">Variance</th>
                              <th className="px-4 py-3 text-center border-r border-white/10">Status</th>
                              <th className="px-4 py-3 text-left">Commentary</th>
                            </tr>
                          </thead>
                          <tbody>
                            {QS.map((q, idx) => {
                              const a = getQActual(detail, q);
                              const t = getEffectiveTarget(detail, q);
                              const v = getEffectiveVariance(detail, q);
                              if (!a && !t) return null;
                              const zebra = idx % 2 === 0 ? 'bg-slate-50/70' : 'bg-white';
                              const cell = 'border-r border-slate-200/70';
                              return (
                                <tr key={q} className={`${zebra} border-b border-slate-200/70 last:border-b-0`}>
                                  <td className={`px-4 py-3 align-middle text-slate-800 font-bold ${cell}`}>{q}</td>
                                  <td className={`px-4 py-3 text-center align-middle tabular-nums text-slate-700 ${cell}`}>{t ?? <span className="text-slate-300">—</span>}</td>
                                  <td className={`px-4 py-3 text-center align-middle tabular-nums font-bold text-slate-800 ${cell}`}>{a?.actualValue ?? <span className="text-slate-300 font-normal">—</span>}</td>
                                  <td className={`px-4 py-3 text-center align-middle ${cell}`}>
                                    {v!=null ? (
                                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tabular-nums ${
                                        v>=0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                                      }`}>
                                        {v>0?'+':''}{v.toFixed(1)}%
                                      </span>
                                    ) : <span className="text-slate-300 text-xs">—</span>}
                                  </td>
                                  <td className={`px-4 py-3 text-center align-middle ${cell}`}>
                                    {a ? (
                                      <span className={`inline-block px-3 py-1 rounded-full text-[10.5px] font-semibold ${approvalBadge(a.approvalStatus)}`}>
                                        {a.approvalStatus}
                                      </span>
                                    ) : <span className="text-slate-300 text-xs">—</span>}
                                  </td>
                                  <td className="px-4 py-3 align-middle text-slate-600 text-xs truncate max-w-[180px]">{a?.commentary || <span className="text-slate-300">—</span>}</td>
                                </tr>
                              );
                            })}
                            {QS.every(q => !getQActual(detail, q) && !getEffectiveTarget(detail, q)) && (
                              <tr><td colSpan={6} className="py-6 text-center text-slate-400 text-xs">No quarterly data yet</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* ── SELECTED QUARTER DETAIL with Approve button ── */}
                    {(() => {
                      const a = getQActual(detail, selQ);
                      if (!a) return null;
                      return (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">{selQ} Detail</p>
                          {a.commentary && (
                            <div>
                              <p className="text-[10px] text-amber-600 uppercase font-semibold tracking-wider">Commentary</p>
                              <p className="text-xs text-slate-700 mt-1">{a.commentary}</p>
                            </div>
                          )}
                          {a.deviationReason && (
                            <div>
                              <p className="text-[10px] text-red-600 uppercase font-semibold tracking-wider">Deviation Reason</p>
                              <p className="text-xs text-slate-700 mt-1">{a.deviationReason}</p>
                            </div>
                          )}
                          {a.correctiveAction && (
                            <div>
                              <p className="text-[10px] text-blue-600 uppercase font-semibold tracking-wider">Corrective Action</p>
                              <p className="text-xs text-slate-700 mt-1">{a.correctiveAction}</p>
                            </div>
                          )}
                          <div className="flex items-center gap-2 pt-2 border-t border-amber-200">
                            {a.approvalStatus === 'Pending' && isExecutive() && (
                              <button onClick={()=>approveActual(a.id)} className="px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-[10px] font-semibold transition-colors">Approve</button>
                            )}
                            <span className="text-[10px] text-amber-700">Submitted: {fmtDate(a.submittedAt)}</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* ── SOURCE / METHOD / BASELINE ── */}
                    <div className="grid grid-cols-3 gap-4 pt-2 border-t border-slate-200">
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">Source / Collection of Data</p>
                        <p className="text-sm text-slate-700 mt-1">{detail.dataSource||'—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">Method of Calculation</p>
                        <p className="text-sm text-slate-700 mt-1">{detail.methodOfCalculation||'—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">Desired Performance / Baseline</p>
                        <p className="text-sm text-slate-700 mt-1">{detail.baseline??'—'}</p>
                      </div>
                    </div>

                    {/* ── LINKED STRATEGIC OBJECTIVE ── */}
                    {detail.strategicObjective && (
                      <div className="bg-red-50/50 rounded-xl p-4">
                        <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">Linked Strategic Objective</p>
                        <p className="text-sm text-slate-700 mt-1">{detail.strategicObjective.reference} — {detail.strategicObjective.title}</p>
                      </div>
                    )}

                    {/* ── EVIDENCE ATTACHMENTS ── */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          Evidence Attachments
                          {(detail.evidence||[]).length > 0 && (
                            <span className="ml-2 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-bold">
                              {(detail.evidence||[]).length}
                            </span>
                          )}
                        </p>
                        <button
                          onClick={()=>{
                            setEvidenceForm({fileName:'',originalFileName:'',description:'',contentType:'application/pdf',fileSizeBytes:0,quarter:selQ,financialYear:fy});
                            setFileData(null);
                            setModal('evidence');
                          }}
                          className="px-3 py-1.5 bg-[#c8102e] text-white rounded-lg text-xs font-semibold hover:bg-[#a00d24] transition-colors"
                        >+ Evidence</button>
                      </div>

                      {/* FEEDBACK FIX (Remove_OWNING_DIVISION2.docx): "Several KPIs with evidence
                          but the system only shows one evidence". Re-shaped this list to:
                          (a) show every row from detail.evidence — flat, never filtered/sliced,
                          (b) group by Quarter so it's instantly obvious how many files exist per period,
                          (c) sort each group by Version desc so the most recent upload is on top. */}
                      {(() => {
                        const all = (detail.evidence || []);
                        if (all.length === 0) {
                          return <p className="text-xs text-slate-400 py-4 text-center">No evidence uploaded</p>;
                        }
                        // Group by Quarter (treat null as "Annual")
                        const groups = all.reduce((acc, ev) => {
                          const k = ev.quarter || 'Annual';
                          (acc[k] = acc[k] || []).push(ev);
                          return acc;
                        }, {});
                        const order = ['Q1','Q2','Q3','Q4','Annual'];
                        const orderedKeys = Object.keys(groups).sort((a,b) => order.indexOf(a) - order.indexOf(b));
                        return orderedKeys.map(qkey => {
                          const items = groups[qkey].slice().sort((a,b) => (b.version||0) - (a.version||0));
                          return (
                            <div key={qkey} className="mb-3 last:mb-0">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-[#c8102e]">{qkey}</span>
                                <span className="text-[9px] text-slate-400">{items.length} file{items.length === 1 ? '' : 's'}</span>
                                <div className="flex-1 h-px bg-slate-100" />
                              </div>
                              <div className="space-y-0">
                                {items.map(ev => (
                                  <div key={ev.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                                    <div className="w-8 h-8 bg-red-50 rounded flex items-center justify-center flex-shrink-0">
                                      <FileText size={16} className="text-[#c8102e]" strokeWidth={2}/>
                                    </div>
                                    <span className="text-sm text-slate-700 flex-1 truncate" title={ev.originalFileName || ev.fileName}>{ev.originalFileName || ev.fileName}</span>
                                    {ev.version > 1 && (
                                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-50 text-blue-700 border border-blue-100 flex-shrink-0">v{ev.version}</span>
                                    )}
                                    <button
                                      onClick={() => openVersionHistory(ev)}
                                      className="text-[10px] text-slate-400 hover:text-[#c8102e] underline-offset-2 hover:underline flex-shrink-0"
                                      title="View version history"
                                    >history</button>
                                    <Paperclip size={15} className="text-slate-400 flex-shrink-0 -rotate-45"/>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>

                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-16 text-center">
                  <BarChart3 size={48} className="mx-auto mb-3 text-slate-200"/>
                  <p className="text-sm text-slate-400">Select a KPI to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          ▓▓▓ DATA CAPTURE · REPORTS · EVIDENCE
          Shared-look colored-border stat cards + dark navy quarter pills.
          ═══════════════════════════════════════════════════════════════ */}
      {(view === 'capture' || view === 'reports' || view === 'evidence') && (
        <div className="p-6 space-y-5">

          {/* ─── COLORED-BORDER STATS ─── */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {(() => {
              const noDataCount = kpis.filter(k=>!getQActual(k,selQ)).length;
              const cards = [
                { l:'Total KPIs',        v: kpis.length,      border:'border-slate-200',   label:'text-slate-500',   num:'text-slate-800' },
                { l:'APP Indicators',    v: appKpis.length,   border:'border-[#c8102e]',   label:'text-[#c8102e]',   num:'text-[#c8102e]'  },
                { l:`On Track (${selQ})`,v: overperforming,   border:'border-emerald-500', label:'text-emerald-600', num:'text-emerald-600'},
                { l:`Behind (${selQ})`,  v: underperforming,  border:'border-[#c8102e]',   label:'text-[#c8102e]',   num:'text-[#c8102e]'  },
                { l:`No Data (${selQ})`, v: noDataCount,      border:'border-slate-200',   label:'text-slate-500',   num:'text-slate-500'  },
              ];
              return cards.map((c,i)=>(
                <div key={i} className={`bg-white rounded-xl border-2 ${c.border} px-5 py-3 shadow-sm`}>
                  <p className={`text-[10px] uppercase tracking-wider font-semibold ${c.label}`}>{c.l}</p>
                  <p className={`text-3xl font-bold ${c.num} leading-tight mt-1`}>{c.v}</p>
                </div>
              ));
            })()}
          </div>

          {/* ─── QUARTER PILLS (dark navy / red) ─── */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600 font-medium mr-1">Quarter:</span>
            {QS.map(q => (
              <button
                key={q}
                onClick={()=>setSelQ(q)}
                className={`px-4 py-1 rounded-full text-xs font-bold transition-all ${
                  selQ === q
                    ? 'bg-[#c8102e] text-white shadow-sm'
                    : 'bg-[#1e2a3d] text-white/80 hover:bg-[#2a3a52]'
                }`}
              >{q}</button>
            ))}
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              ▓ DATA CAPTURE VIEW ▓
              ═══════════════════════════════════════════════════════════════ */}
          {view === 'capture' && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
                <h3 className="font-bold text-sm text-slate-800 uppercase tracking-wider">
                  {selQ} FY{fy-1}/{fy%100} — Quarterly Actuals Capture
                </h3>
                <p className="text-xs text-slate-500">
                  {kpis.length} KPIs · {kpis.filter(k=>!getQActual(k,selQ)).length} awaiting actuals
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    {/* Column sizing: REF fixed, KPI flex, metric columns compact, ACTION fixed */}
                    <colgroup>
                      <col className="w-[110px]"/>
                      <col/>
                      <col className="w-[88px]"/>
                      <col className="w-[108px]"/>
                      <col className="w-[88px]"/>
                      <col className="w-[88px]"/>
                      <col className="w-[120px]"/>
                      <col className="w-[120px]"/>
                      <col className="w-[120px]"/>
                    </colgroup>

                    <thead>
                      <tr className="bg-[#1e2a3d] text-[10.5px] text-white/70 uppercase tracking-[0.08em] font-semibold">
                        <th className="px-6 py-4 text-left border-r border-white/10">Ref</th>
                        <th className="px-4 py-4 text-left border-r border-white/10">KPI</th>
                        <th className="px-3 py-4 text-center border-r border-white/10">Unit</th>
                        <th className="px-3 py-4 text-center border-r border-white/10">Unit Fn</th>
                        <th className="px-3 py-4 text-center border-r border-white/10">Target</th>
                        <th className="px-3 py-4 text-center border-r border-white/10">Actual</th>
                        <th className="px-3 py-4 text-center border-r border-white/10">Variance</th>
                        <th className="px-3 py-4 text-center border-r border-white/10">Status</th>
                        <th className="px-6 py-4 text-center">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filtered.map((k, idx)=>{
                        const a = getQActual(k,selQ);
                        const t = getEffectiveTarget(k,selQ);
                        const v = getEffectiveVariance(k,selQ);
                        // Interchanged: even rows now get the gray tint, odd rows stay white
                        const zebra = idx % 2 === 0 ? 'bg-slate-50/70' : 'bg-white';
                        const cell = 'border-r border-slate-200/70';
                        return (
                          <tr
                            key={k.id}
                            className={`${zebra} hover:bg-[#c8102e]/[0.04] transition-colors group border-b border-slate-200/70 last:border-b-0`}
                          >
                            {/* REF — as a chip */}
                            <td className={`px-6 py-4 align-middle ${cell}`}>
                              <span className="inline-block px-2 py-0.5 bg-white text-slate-700 border border-slate-200 rounded-md font-mono text-[10px] font-bold tracking-wide whitespace-nowrap">
                                {k.reference}
                              </span>
                            </td>

                            {/* KPI — name + meta */}
                            <td className={`px-4 py-4 align-middle ${cell}`}>
                              <p className="text-sm text-slate-800 font-semibold leading-snug">{k.name}</p>
                              <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">
                                <span className="truncate">{k.division?.name||'—'}</span>
                                <span className="text-slate-300">·</span>
                                <span className="truncate">{k.responsibleOwner||'—'}</span>
                              </p>
                            </td>

                            {/* UNIT / UNIT FN */}
                            <td className={`px-3 py-4 text-center align-middle ${cell}`}>
                              <span className="text-xs text-slate-500">{k.unit||'—'}</span>
                            </td>
                            <td className={`px-3 py-4 text-center align-middle ${cell}`}>
                              <span className="text-xs text-slate-500">{UNIT_FUNCTION_LABELS[k.unitFunction]||'—'}</span>
                            </td>

                            {/* TARGET / ACTUAL — tabular nums for perfect vertical alignment */}
                            <td className={`px-3 py-4 text-center align-middle tabular-nums text-sm font-bold text-slate-800 ${cell}`}>
                              {t ?? <span className="text-slate-300 font-normal">—</span>}
                            </td>
                            <td className={`px-3 py-4 text-center align-middle tabular-nums text-sm font-bold text-slate-800 ${cell}`}>
                              {a?.actualValue ?? <span className="text-slate-300 font-normal">—</span>}
                            </td>

                            {/* VARIANCE */}
                            <td className={`px-3 py-4 text-center align-middle ${cell}`}>
                              {v!=null ? (
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tabular-nums ${
                                  v>=0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                                }`}>
                                  {v>0?'+':''}{v.toFixed(1)}%
                                </span>
                              ) : <span className="text-slate-300 text-xs">—</span>}
                            </td>

                            {/* STATUS */}
                            <td className={`px-3 py-4 text-center align-middle ${cell}`}>
                              {a ? (
                                <span className={`inline-block px-3 py-1 rounded-full text-[10.5px] font-semibold ${approvalBadge(a.approvalStatus)}`}>
                                  {a.approvalStatus}
                                </span>
                              ) : <span className="text-slate-300 text-xs">—</span>}
                            </td>

                            {/* ACTION */}
                            <td className="px-6 py-4 text-center align-middle">
                              {!a ? (
                                <button
                                  onClick={()=>{
                                    loadDetail(k.id);
                                    setActualForm({...emptyActual,financialYear:fy,quarter:selQ,targetValue:t?.toString()||''});
                                    setModal('actual');
                                  }}
                                  className="px-4 py-1.5 bg-[#c8102e] text-white rounded-md text-[11px] font-semibold hover:bg-[#a00d24] transition-all shadow-sm hover:shadow"
                                >Capture</button>
                              ) : (
                                <div className="w-7 h-7 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
                                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" className="text-emerald-600">
                                    <path d="M2.5 6.8 L5.3 9.5 L10.5 3.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {filtered.length === 0 && (
                        <tr><td colSpan={9} className="py-16 text-center text-slate-400 text-sm">No KPIs match your filter</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ── FOOTER: Save Draft ── */}
                {filtered.length > 0 && (
                  <div className="flex items-center justify-end gap-2 px-6 py-3 bg-slate-50/70 border-t border-slate-200">
                    <button
                      onClick={()=>alert('Draft saved')}
                      className="px-5 py-2 bg-[#c8102e] text-white rounded-md text-xs font-semibold hover:bg-[#a00d24] transition-colors shadow-sm"
                    >Save Draft</button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              ▓ REPORTS VIEW ▓
              ═══════════════════════════════════════════════════════════════ */}
          {view === 'reports' && (
            <>
              {/* Generate Consolidated Report — big silvery-gray container */}
              <div className="bg-gradient-to-br from-slate-100 to-slate-200/60 rounded-2xl p-6 border border-slate-200/70">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-4">Generate Consolidated Report</p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {QS.map(q => (
                    <button
                      key={q}
                      onClick={()=>generateReport('APP_Quarterly',q)}
                      className="bg-white border border-slate-200 rounded-xl py-6 text-center hover:shadow-md hover:-translate-y-0.5 transition-all"
                    >
                      <p className="text-2xl font-bold text-slate-800">{q}</p>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1.5">APP Quarterly</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Generated Reports — same container treatment */}
              <div className="bg-gradient-to-br from-slate-100 to-slate-200/60 rounded-2xl p-6 border border-slate-200/70">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-4">
                  Generated Reports ({reports.length})
                </p>
                <div className="space-y-2">
                  {reports.map(r => (
                    <div
                      key={r.id}
                      className="flex items-center gap-4 px-4 py-3.5 bg-gradient-to-r from-slate-50 to-slate-100/40 rounded-lg hover:shadow-sm transition-all cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                        <FileText size={22} className="text-[#c8102e]" strokeWidth={1.5}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{r.title}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {r.reportType} · Generated {fmtDate(r.generatedAt)}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-[11px] font-semibold ${approvalBadge(r.approvalStatus)}`}>
                        {r.approvalStatus}
                      </span>
                    </div>
                  ))}
                  {reports.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-8">No reports generated yet</p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              ▓ EVIDENCE VIEW ▓
              ═══════════════════════════════════════════════════════════════ */}
          {view === 'evidence' && (
            <div className="bg-gradient-to-br from-slate-100 to-slate-200/60 rounded-2xl p-6 border border-slate-200/70">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    Evidence Repository
                    {evidenceList.length > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full bg-[#c8102e] text-white text-[10px] font-bold">
                        {evidenceList.length} file{evidenceList.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {detail ? `${detail.reference} — ${detail.name}` : 'Select a KPI from the KPI Data tab to manage evidence'}
                  </p>
                </div>
                {detail && (
                  <button
                    onClick={()=>{
                      setEvidenceForm({fileName:'',originalFileName:'',description:'',contentType:'',fileSizeBytes:0,quarter:selQ,financialYear:fy});
                      setFileData(null);
                      setModal('evidence');
                    }}
                    className="px-4 py-2 bg-[#c8102e] text-white rounded-lg text-xs font-semibold hover:bg-[#a00d24] transition-colors shadow-sm"
                  >+ Upload Evidence</button>
                )}
              </div>

              {detail ? (
                <div className="space-y-2">
                  {evidenceList.map(ev => (
                    <div
                      key={ev.id}
                      className="flex items-center gap-4 px-4 py-3.5 bg-gradient-to-r from-slate-50 to-slate-100/40 rounded-lg hover:shadow-sm transition-all"
                    >
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                        <FileText size={22} className="text-[#c8102e]" strokeWidth={1.5}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{ev.originalFileName || ev.fileName}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Version {ev.version} · {ev.quarter ? `${ev.quarter} ` : ''}FY{ev.financialYear} · {(ev.fileSizeBytes/1024).toFixed(0)} KB · {fmtDate(ev.createdAt)}
                        </p>
                        {ev.description && <p className="text-[11px] text-slate-400 mt-0.5 truncate">{ev.description}</p>}
                      </div>
                      <button
                        onClick={() => openVersionHistory(ev)}
                        className="px-2.5 py-1 rounded-md text-[10px] font-semibold text-slate-600 bg-white border border-slate-200 hover:border-[#c8102e] hover:text-[#c8102e] flex-shrink-0"
                        title="View version history for this KPI/quarter"
                      >View History</button>
                      <Paperclip size={15} className="text-slate-400 flex-shrink-0 -rotate-45" />
                      <span className={`px-3 py-1 rounded-full text-[11px] font-semibold ${approvalBadge(ev.approvalStatus)}`}>
                        {ev.approvalStatus}
                      </span>
                    </div>
                  ))}
                  {evidenceList.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-8">No evidence uploaded for this KPI</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-12">Select a KPI from the KPI Data tab first</p>
              )}
            </div>
          )}

        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          MODALS (kept intact)
          ═══════════════════════════════════════════════════════════════ */}
      {canEditKpiDefs && (
        <Modal open={modal==='kpi'} onClose={()=>setModal(null)} title={editingId?'Edit KPI':'New KPI Definition'} wide>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><label className={label}>KPI Name *</label><input className={input} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></div>
            <div className="md:col-span-2"><label className={label}>Description</label><textarea className={input+' h-16'} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/></div>
            <div><label className={label}>Type</label><select className={input} value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}><option>Quantitative</option><option>Qualitative</option></select></div>
            <div><label className={label}>Reporting Cycle</label><select className={input} value={form.frequency} onChange={e=>setForm(f=>({...f,frequency:e.target.value}))}><option>Quarterly</option><option>Annually</option></select></div>
            <div><label className={label}>Unit *</label><select className={input} value={form.unit} onChange={e=>setForm(f=>({...f,unit:e.target.value}))}><option value="">— Select —</option>{UNITS.map(u=><option key={u}>{u}</option>)}</select></div>
            <div><label className={label}>Unit Function *</label><select className={input} value={form.unitFunction} onChange={e=>setForm(f=>({...f,unitFunction:e.target.value}))}><option value="">— Select —</option>{UNIT_FUNCTIONS.map(u=><option key={u} value={u}>{UNIT_FUNCTION_LABELS[u]}</option>)}</select></div>
            <div><label className={label}>Strategic Objective *</label><select className={input} value={form.strategicObjectiveId} onChange={e=>setForm(f=>({...f,strategicObjectiveId:e.target.value}))}><option value="">— Required —</option>{objectives.map(o=><option key={o.id} value={o.id}>{o.reference} — {o.title}</option>)}</select></div>
            <div><label className={label}>Division</label><select className={input} value={form.divisionId} onChange={e=>setForm(f=>({...f,divisionId:e.target.value}))}><option value="">—</option>{divisions.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
            <div><label className={label}>Responsible Owner</label><select className={input} value={form.responsibleOwner} onChange={e=>setForm(f=>({...f,responsibleOwner:e.target.value}))}><option value="">— Select —</option>{RESPONSIBLE_OWNERS.map(o=><option key={o}>{o}</option>)}</select></div>
            <div><label className={label}>Financial Year</label><select className={input} value={form.financialYear} onChange={e=>setForm(f=>({...f,financialYear:e.target.value}))}><option value={2026}>2025/26</option><option value={2027}>2026/27</option></select></div>
            <div className="md:col-span-2 bg-slate-50 rounded-xl p-4"><p className="text-xs font-semibold text-slate-500 uppercase mb-3">Targets</p>
              {isQualitative ? (
                <div className="grid grid-cols-5 gap-3"><div><label className={label}>Annual</label><input className={input} value={form.qualitativeAnnualTarget||''} onChange={e=>setForm(f=>({...f,qualitativeAnnualTarget:e.target.value}))} placeholder="e.g. Clean Audit"/></div><div><label className={label}>Q1</label><input className={input} value={form.qualitativeQ1Target||''} onChange={e=>setForm(f=>({...f,qualitativeQ1Target:e.target.value}))}/></div><div><label className={label}>Q2</label><input className={input} value={form.qualitativeQ2Target||''} onChange={e=>setForm(f=>({...f,qualitativeQ2Target:e.target.value}))}/></div><div><label className={label}>Q3</label><input className={input} value={form.qualitativeQ3Target||''} onChange={e=>setForm(f=>({...f,qualitativeQ3Target:e.target.value}))}/></div><div><label className={label}>Q4</label><input className={input} value={form.qualitativeQ4Target||''} onChange={e=>setForm(f=>({...f,qualitativeQ4Target:e.target.value}))}/></div></div>
              ) : (
                <div className="grid grid-cols-5 gap-3"><div><label className={label}>Annual</label><input className={input} type="number" value={form.annualTarget} onChange={e=>setForm(f=>({...f,annualTarget:e.target.value}))}/></div><div><label className={label}>Q1</label><input className={input} type="number" value={form.q1Target} onChange={e=>setForm(f=>({...f,q1Target:e.target.value}))}/></div><div><label className={label}>Q2</label><input className={input} type="number" value={form.q2Target} onChange={e=>setForm(f=>({...f,q2Target:e.target.value}))}/></div><div><label className={label}>Q3</label><input className={input} type="number" value={form.q3Target} onChange={e=>setForm(f=>({...f,q3Target:e.target.value}))}/></div><div><label className={label}>Q4</label><input className={input} type="number" value={form.q4Target} onChange={e=>setForm(f=>({...f,q4Target:e.target.value}))}/></div></div>
              )}
            </div>
            <div><label className={label}>Desired Performance / Baseline</label><input className={input} type="number" value={form.baseline} onChange={e=>setForm(f=>({...f,baseline:e.target.value}))}/></div>
            <div><label className={label}>Source / Collection of Data</label><input className={input} value={form.dataSource} onChange={e=>setForm(f=>({...f,dataSource:e.target.value}))}/></div>
            <div className="md:col-span-2"><label className={label}>Method of Calculation / Assessment</label><input className={input} value={form.methodOfCalculation||''} onChange={e=>setForm(f=>({...f,methodOfCalculation:e.target.value}))}/></div>
          </div>
          <div className="flex justify-end gap-2 mt-6"><button onClick={()=>setModal(null)} className={btnS}>Cancel</button><button onClick={saveKpi} className={btnP}>{editingId?'Update':'Create'} KPI</button></div>
        </Modal>
      )}

      <Modal open={modal==='actual'} onClose={()=>setModal(null)} title={`Capture ${actualForm.quarter} Actuals`}>
        <div className="space-y-3">
          {detail?.type==='Qualitative' ? (
            <div><label className={label}>Qualitative Actual *</label><input className={input} value={actualForm.qualitativeActualValue||''} onChange={e=>setActualForm(f=>({...f,qualitativeActualValue:e.target.value}))} placeholder="e.g. Accreditation Maintained" autoFocus/></div>
          ) : (
            <div className="grid grid-cols-2 gap-3"><div><label className={label}>Target</label><input className={input} type="number" value={actualForm.targetValue} onChange={e=>setActualForm(f=>({...f,targetValue:e.target.value}))}/></div><div><label className={label}>Actual *</label><input className={input} type="number" value={actualForm.actualValue} onChange={e=>setActualForm(f=>({...f,actualValue:e.target.value}))} autoFocus/></div></div>
          )}
          <div><label className={label}>Commentary *</label><textarea className={input+' h-16'} value={actualForm.commentary} onChange={e=>setActualForm(f=>({...f,commentary:e.target.value}))} placeholder="Narrative explanation of performance..."/></div>
          <div><label className={label}>Deviation Reason (if behind target)</label><textarea className={input+' h-12'} value={actualForm.deviationReason} onChange={e=>setActualForm(f=>({...f,deviationReason:e.target.value}))}/></div>
          <div><label className={label}>Corrective Action</label><textarea className={input+' h-12'} value={actualForm.correctiveAction} onChange={e=>setActualForm(f=>({...f,correctiveAction:e.target.value}))}/></div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><button onClick={()=>setModal(null)} className={btnS}>Cancel</button><button onClick={saveActual} className={btnP}>Submit {actualForm.quarter} Actuals</button></div>
      </Modal>

      <Modal open={modal==='evidence'} onClose={()=>setModal(null)} title="Upload Evidence Document">
        <div className="space-y-3">
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-[#c8102e]/50 transition-colors">
            <input type="file" onChange={handleFileSelect} className="mb-2" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.ppt,.pptx" />
            <p className="text-[10px] text-slate-400 mt-1">Supported: PDF, Word, Excel, PowerPoint, Images</p>
            {fileData && <p className="text-xs text-emerald-600 mt-2 font-semibold">✓ File selected: {evidenceForm.originalFileName} ({(evidenceForm.fileSizeBytes/1024).toFixed(0)} KB)</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Quarter</label><select className={input} value={evidenceForm.quarter} onChange={e=>setEvidenceForm(f=>({...f,quarter:e.target.value}))}><option value="">—</option>{QS.map(q=><option key={q}>{q}</option>)}</select></div>
            <div><label className={label}>Financial Year</label><select className={input} value={evidenceForm.financialYear} onChange={e=>setEvidenceForm(f=>({...f,financialYear:e.target.value}))}><option value={2026}>2025/26</option><option value={2027}>2026/27</option></select></div>
          </div>
          <div><label className={label}>Description</label><textarea className={input+' h-12'} value={evidenceForm.description} onChange={e=>setEvidenceForm(f=>({...f,description:e.target.value}))} placeholder="Brief description of the evidence document"/></div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><button onClick={()=>setModal(null)} className={btnS}>Cancel</button><button onClick={saveEvidence} className={btnP} disabled={!fileData&&!evidenceForm.fileName}>Upload Evidence</button></div>
      </Modal>

      {/* V3.1 — Evidence Version History */}
      <Modal
        open={modal === 'history'}
        onClose={() => { setModal(null); setHistoryData(null); setHistoryContext(null); }}
        title="Evidence Version History"
        wide
      >
        {historyContext && (
          <div className="bg-slate-50 rounded-xl px-4 py-3 mb-4 border border-slate-200">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Context</p>
            <div className="flex items-center gap-2 flex-wrap">
              {historyContext.kpiReference && <span className="font-mono text-[11px] text-[#c8102e] bg-red-50 px-2 py-0.5 rounded">{historyContext.kpiReference}</span>}
              <span className="text-sm font-semibold text-slate-800">{historyContext.kpiName || '—'}</span>
              <span className="text-slate-300">·</span>
              <span className="text-xs text-slate-600">{historyContext.quarter || '—'} FY{historyContext.financialYear || '—'}</span>
            </div>
          </div>
        )}

        {historyLoading && (
          <div className="text-center py-12 text-slate-400 text-sm">Loading version history…</div>
        )}

        {!historyLoading && historyData && (
          <>
            <div className="flex items-center gap-3 mb-4 text-xs">
              <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold">Current: v{historyData.currentVersion}</span>
              <span className="text-slate-500">{historyData.totalVersions} version{historyData.totalVersions === 1 ? '' : 's'} on file</span>
            </div>

            {historyData.versions.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No versions found.</p>
            ) : (
              <div className="relative">
                {/* Vertical timeline line */}
                <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-slate-200" />

                <div className="space-y-3">
                  {historyData.versions.map((v, idx) => {
                    const isCurrent = idx === 0;
                    return (
                      <div key={v.id} className="relative flex items-start gap-4">
                        {/* Version dot */}
                        <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${isCurrent ? 'bg-[#c8102e] text-white shadow-lg shadow-[#c8102e]/30 ring-4 ring-red-100' : 'bg-white text-slate-600 border-2 border-slate-300'}`}>
                          v{v.version}
                        </div>

                        <div className={`flex-1 rounded-xl p-3.5 border ${isCurrent ? 'bg-white border-[#c8102e]/30 shadow-sm' : 'bg-slate-50/70 border-slate-200'}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <FileText size={14} className="text-[#c8102e] shrink-0" />
                                <p className="text-sm font-semibold text-slate-800 truncate">{v.originalFileName || v.fileName}</p>
                                {isCurrent && <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">Current</span>}
                              </div>
                              <p className="text-[11px] text-slate-500 mt-1">
                                Uploaded by <strong className="text-slate-700">{v.uploadedBy || '—'}</strong>
                                {' · '}{fmtDate(v.createdAt)}
                                {v.fileSizeBytes != null && <> · {(v.fileSizeBytes / 1024).toFixed(0)} KB</>}
                              </p>
                              {v.description && (
                                <p className="text-[11px] text-slate-600 mt-1.5 italic border-l-2 border-slate-200 pl-2">"{v.description}"</p>
                              )}
                            </div>
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold shrink-0 ${approvalBadge(v.approvalStatus)}`}>{v.approvalStatus}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex justify-end mt-4">
          <button onClick={() => { setModal(null); setHistoryData(null); setHistoryContext(null); }} className={btnS}>Close</button>
        </div>
      </Modal>
    </div>
    </>
  );
}
